<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core'

import type { MiniGame, TeamStructure, Theme } from '~/stores/challengeWheel'

import { BOYS_GIRLS_TEAMS, COLOR_TEAMS, CUSTOM_COLORS, MINI_GAME_CONFIG } from '~/stores/challengeWheel'

const emit = defineEmits<{ start: [] }>()

const store = useChallengeWheelStore()
const { data: ui } = useChallengeWheelUI()

// ── Option lists driven by content ──────────────────────
const themes = computed(() => ui.value?.setup.theme.options ?? [])
const teamStructures = computed(() => ui.value?.setup.teamStructure.structures ?? [])
const allMiniGames = computed(() => ui.value?.setup.miniGames.games ?? [])
const teamNameLabel = computed(() => ui.value?.setup.teamStructure.teamNameLabel ?? 'Team')

// ── Persisted setup options ──────────────────────────────
const theme = useLocalStorage<Theme>('sfg.challenge-wheel.theme', 'bible')
const teamStructure = useLocalStorage<TeamStructure>('sfg.challenge-wheel.teamStructure', 'color-teams')
const customTeamCount = useLocalStorage<number>('sfg.challenge-wheel.customTeamCount', 4)
const customTeamNames = useLocalStorage<string[]>(
  'sfg.challenge-wheel.customTeamNames',
  Array.from({ length: 6 }, (_, i) => `${teamNameLabel.value} ${i + 1}`),
)
const selectedGames = useLocalStorage<MiniGame[]>(
  'sfg.challenge-wheel.selectedGames',
  [ 'sword-drill', 'bible-trivia', 'categories', 'word-scramble' ],
)

const teamCountOptions = [ 2, 3, 4, 5, 6 ]

// ── Helpers ──────────────────────────────────────────────
const MIN_SELECTED_GAMES = 2

function toggleGame(game: MiniGame) {
  const current = selectedGames.value
  const idx = current.indexOf(game)
  if (idx >= 0) {
    selectedGames.value = current.filter(g => g !== game)
  } else {
    selectedGames.value = [ ...current, game ]
  }
}

const isGameSelected = (g: MiniGame) => selectedGames.value.includes(g)
const canStart = computed(() => selectedGames.value.length >= MIN_SELECTED_GAMES)

const colorTeamsPreview = COLOR_TEAMS
const boysGirlsPreview = BOYS_GIRLS_TEAMS

// ── Reset handler ─────────────────────────────────────
function handleReset() {
  theme.value = 'bible'
  teamStructure.value = 'color-teams'
  customTeamCount.value = 4
  customTeamNames.value = Array.from({ length: 6 }, (_, i) => `${teamNameLabel.value} ${i + 1}`)
  selectedGames.value = [ 'sword-drill', 'bible-trivia', 'categories', 'word-scramble' ]
}

defineExpose({ handleReset })

// ── Start handler ──────────────────────────────────────
function handleStart() {
  store.config.theme = theme.value
  store.config.teamStructure = teamStructure.value
  store.config.customTeamCount = customTeamCount.value
  store.config.customTeamNames = [ ...customTeamNames.value ]
  store.config.selectedGames = [ ...selectedGames.value ]
  emit('start')
}
</script>

<template>
  <div class="space-y-8">
    <!-- ── Theme ──────────────────────────────────────────── -->
    <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
      <template #header>
        <div class="flex items-center gap-3">
          <UIcon
            class="text-primary size-5 shrink-0"
            name="mdi:palette-outline"
          />
          <div>
            <h2 class="text-lg font-semibold">
              {{ ui?.setup.theme.title }}
            </h2>
            <p class="text-sm text-muted">
              {{ ui?.setup.theme.description }}
            </p>
          </div>
        </div>
      </template>
      <div class="flex flex-wrap gap-3">
        <UButton
          v-for="t in themes"
          :key="t.value"
          :color="theme === t.value ? 'primary' : 'neutral'"
          :label="t.label"
          :leading-icon="t.icon"
          :variant="theme === t.value ? 'solid' : 'outline'"
          @click="theme = t.value"
        />
      </div>
    </UCard>

    <!-- ── Team Structure ─────────────────────────────────── -->
    <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
      <template #header>
        <div class="flex items-center gap-3">
          <UIcon
            class="text-primary size-5 shrink-0"
            name="mdi:account-group-outline"
          />
          <div>
            <h2 class="text-lg font-semibold">
              {{ ui?.setup.teamStructure.title }}
            </h2>
            <p class="text-sm text-muted">
              {{ ui?.setup.teamStructure.description }}
            </p>
          </div>
        </div>
      </template>

      <div class="space-y-4">
        <!-- Structure selector -->
        <div class="flex flex-wrap gap-3">
          <button
            v-for="ts in teamStructures"
            :key="ts.value"
            :class="[
              'rounded-xl border-2 px-5 py-3 text-left transition-colors',
              teamStructure === ts.value
                ? 'border-primary bg-primary/10 font-semibold text-primary'
                : 'border-muted hover:border-primary/50',
            ]"
            @click="teamStructure = ts.value"
          >
            <div class="font-semibold">
              {{ ts.label }}
            </div>
            <div class="text-xs text-muted">
              {{ ts.description }}
            </div>
          </button>
        </div>

        <!-- Color Teams preview -->
        <div
          v-if="teamStructure === 'color-teams'"
          class="flex flex-wrap gap-2 pt-1"
        >
          <div
            v-for="team in colorTeamsPreview"
            :key="team.name"
            :style="{ backgroundColor: team.color }"
            class="rounded-lg px-4 py-2 text-sm font-bold text-white shadow"
          >
            {{ team.name }}
          </div>
        </div>

        <!-- Boys v Girls preview -->
        <div
          v-else-if="teamStructure === 'boys-v-girls'"
          class="flex flex-wrap gap-2 pt-1"
        >
          <div
            v-for="team in boysGirlsPreview"
            :key="team.name"
            :style="{ backgroundColor: team.color }"
            class="rounded-lg px-4 py-2 text-sm font-bold text-white shadow"
          >
            {{ team.name }}
          </div>
        </div>

        <!-- Custom options -->
        <div
          v-else-if="teamStructure === 'custom'"
          class="space-y-4 pt-2"
        >
          <!-- Team count -->
          <div class="flex flex-wrap items-center gap-3">
            <span class="min-w-32 text-sm font-medium text-muted">{{ ui?.setup.teamStructure.numberOfTeamsLabel }}</span>
            <div class="flex gap-2">
              <UButton
                v-for="n in teamCountOptions"
                :key="n"
                :color="customTeamCount === n ? 'primary' : 'neutral'"
                :label="String(n)"
                :variant="customTeamCount === n ? 'solid' : 'outline'"
                size="sm"
                @click="customTeamCount = n"
              />
            </div>
          </div>

          <!-- Team name inputs -->
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <UFormField
              v-for="i in customTeamCount"
              :key="i"
              :label="`${teamNameLabel} ${i}`"
            >
              <div class="flex items-center gap-2">
                <span
                  :style="{ backgroundColor: CUSTOM_COLORS[(i - 1) % CUSTOM_COLORS.length] }"
                  class="size-3 shrink-0 rounded-full"
                />
                <UInput
                  v-model="customTeamNames[i - 1]"
                  :placeholder="`${teamNameLabel} ${i}`"
                  class="w-full"
                />
              </div>
            </UFormField>
          </div>
        </div>
      </div>
    </UCard>

    <!-- ── Mini-game Selection ────────────────────────────── -->
    <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
      <template #header>
        <div class="flex items-center gap-3">
          <UIcon
            class="text-primary size-5 shrink-0"
            name="mdi:gamepad-variant-outline"
          />
          <div>
            <h2 class="text-lg font-semibold">
              {{ ui?.setup.miniGames.title }}
            </h2>
            <p class="text-sm text-muted">
              {{ ui?.setup.miniGames.description }}
            </p>
          </div>
        </div>
      </template>

      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <button
          v-for="game in allMiniGames"
          :key="game.value"
          :class="[
            'flex flex-col items-center gap-2 rounded-xl border-2 px-4 py-5 text-center transition-colors',
            isGameSelected(game.value)
              ? 'border-primary bg-primary/10 font-semibold'
              : 'border-muted opacity-60 hover:opacity-80',
          ]"
          :style="isGameSelected(game.value)
            ? { borderColor: MINI_GAME_CONFIG[game.value].color, backgroundColor: MINI_GAME_CONFIG[game.value].color + '18' }
            : {}"
          @click="toggleGame(game.value)"
        >
          <UIcon
            :name="game.icon"
            :style="{ color: MINI_GAME_CONFIG[game.value].color }"
            class="size-8"
          />
          <span class="text-sm font-semibold">{{ game.label }}</span>
        </button>
      </div>
    </UCard>

    <!-- ── Start Button ──────────────────────────────────── -->
    <div class="flex flex-col items-center gap-3 pt-2">
      <p
        v-if="!canStart"
        class="text-sm text-warning"
      >
        {{ ui?.setup.miniGames.minGamesHint }}
      </p>
      <UButton
        :disabled="!canStart"
        icon="mdi:play-circle"
        :label="ui?.setup.startButton"
        size="xl"
        @click="handleStart"
      />
    </div>
  </div>
</template>
